/**
 * O-08 «Выбор города/региона».
 *
 * ПС §07, BS §17.6 `GET /geo/cities` (`q`, `country`, `limit`,
 * `Accept-Language`): поиск начинается от двух символов и показывает не
 * больше 20 подсказок — справочник большой, а список без поиска превращается
 * в случайную выборку.
 *
 * Точные координаты клиент не хранит и не передаёт: город — единственная
 * форма местоположения (BS §11.2, инвариант I-7). Изменение v1.5: по городам
 * идёт и сам подбор — километров в продукте больше нет.
 */
import { useEffect, useState } from 'react'

import { api, unwrap } from '@shared/api/client'
import type { City } from '@shared/api/types'
import { EmptyState, Icon, SearchInput, Sheet, Spinner, cx } from '@shared/ui'

import styles from './city.module.css'

/** BS §17.6: поиск от двух символов, не больше 20 подсказок. */
const MIN_QUERY = 2
const LIMIT = 20
const DEBOUNCE_MS = 250

export function CityPicker({
  open,
  selectedId,
  selectedIds,
  title = 'Город',
  onSelect,
  onClose,
}: {
  open: boolean
  selectedId: string | null
  /** Уже выбранные города — отмечаются галочкой (список городов подбора, v1.5). */
  selectedIds?: readonly string[]
  title?: string
  onSelect: (city: City) => void
  onClose: () => void
}): React.ReactElement {
  const chosen = new Set(selectedIds ?? (selectedId ? [selectedId] : []))
  const [query, setQuery] = useState('')
  const [items, setItems] = useState<City[]>([])
  const [loading, setLoading] = useState(false)

  const ready = query.trim().length >= MIN_QUERY

  useEffect(() => {
    if (!open) {
      setQuery('')
      setItems([])
      return
    }
  }, [open])

  useEffect(() => {
    if (!ready) {
      setItems([])
      return
    }

    let cancelled = false
    setLoading(true)

    const timer = setTimeout(() => {
      void (async () => {
        try {
          const page = unwrap(
            await api.GET('/geo/cities', {
              params: { query: { q: query.trim(), limit: LIMIT } as never },
            }),
          )
          if (!cancelled) setItems(page.items)
        } catch {
          if (!cancelled) setItems([])
        } finally {
          if (!cancelled) setLoading(false)
        }
      })()
    }, DEBOUNCE_MS)

    return () => {
      cancelled = true
      clearTimeout(timer)
    }
  }, [query, ready])

  return (
    <Sheet open={open} onClose={onClose} title={title} description="Другим виден только город.">
      <div className={styles['search']}>
        <SearchInput
          value={query}
          onValueChange={setQuery}
          aria-label="Поиск города"
          placeholder="Начните вводить название"
        />
      </div>

      {!ready ? (
        <p className={styles['hint']}>Введите хотя бы {MIN_QUERY} символа.</p>
      ) : loading && !items.length ? (
        <Spinner size={24} label="Ищем" />
      ) : items.length ? (
        <div className={styles['list']}>
          {items.map((city) => (
            <button
              key={city.id}
              type="button"
              className={cx(styles['row'], chosen.has(city.id) && styles['rowActive'])}
              onClick={() => {
                onSelect(city)
                onClose()
              }}
            >
              <Icon name="pin" size={18} />
              <span className={styles['name']}>
                {city.name}
                {city.admin_area ? <span className={styles['area']}>{city.admin_area}</span> : null}
              </span>
              {chosen.has(city.id) ? <Icon name="check" size={18} /> : null}
            </button>
          ))}
        </div>
      ) : (
        <EmptyState icon="search" title="Город не найден" text="Проверьте написание или укажите ближайший." />
      )}
    </Sheet>
  )
}
